import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { t } from '../../i18n/index.js';
import { FeedbackContext } from './context.js';
import { Spinner } from './Spinner.jsx';
import styles from './Feedback.module.css';

const DEFAULT_DURATIONS = { success: 5000, info: 5000, error: 8000 };

// Wrap each app's <App/> once. `lang` comes from the app's LanguageContext
// (see admin/portal src/components/AppFeedback.jsx) and is used only for
// this component's own chrome — dismiss labels, the confirm dialog's default
// OK/Cancel. Every message/title passed to useToast()/useConfirm() is
// already translated by the caller.
export function FeedbackProvider({ lang, children }) {
  // --- Toasts ---------------------------------------------------------
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((toastItem) => toastItem.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (kind, message, duration) => {
      idRef.current += 1;
      const id = idRef.current;
      setToasts((list) => [...list, { id, kind, message }]);
      const ms = duration ?? DEFAULT_DURATIONS[kind] ?? 5000;
      if (ms > 0) {
        timers.current.set(
          id,
          setTimeout(() => dismiss(id), ms)
        );
      }
      return id;
    },
    [dismiss]
  );

  useEffect(
    () => () => {
      timers.current.forEach((timer) => clearTimeout(timer));
      timers.current.clear();
    },
    []
  );

  const toast = useMemo(
    () => ({
      success: (message, opts) => push('success', message, opts?.duration),
      error: (message, opts) => push('error', message, opts?.duration),
      info: (message, opts) => push('info', message, opts?.duration),
      show: ({ kind = 'info', message, duration }) => push(kind, message, duration),
      dismiss,
    }),
    [push, dismiss]
  );

  // --- Confirm dialog (promise-based, one at a time) ---------------------
  const [dialog, setDialog] = useState(null);
  const dialogRef = useRef(null);
  const cancelButtonRef = useRef(null);
  const confirmButtonRef = useRef(null);
  const typedInputRef = useRef(null);

  // Bulk-delete confirmation (customers/products/users multi-select task):
  // `options.requireTypedConfirmation` is `{ expected }` — `expected` is
  // usually the selection count, coerced to a string once here so every
  // comparison below is a plain string compare, never a number/string
  // mismatch. When present, Confirm stays disabled until the typed value
  // matches EXACTLY (trimmed) — this is the "must type the number, not just
  // click OK" gate root CLAUDE.md's rebuild task made non-negotiable for
  // bulk delete. Every other confirm() caller (there are many, none of them
  // pass this option) is completely unaffected — `typedValue` only ever
  // matters while `dialog.requireTypedConfirmation` is set.
  const [typedValue, setTypedValue] = useState('');

  const confirm = useCallback(
    (options) =>
      new Promise((resolve) => {
        setTypedValue('');
        setDialog({ ...options, resolve });
      }),
    []
  );

  const settle = useCallback((result) => {
    setDialog((current) => {
      if (current) current.resolve(result);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!dialog) return undefined;

    const previousActive = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus starts on the typed-confirmation input when one is required
    // (that's the actual next step), on Cancel for any other danger action,
    // on Confirm otherwise.
    if (dialog.requireTypedConfirmation) {
      typedInputRef.current?.focus();
    } else {
      const startTarget = dialog.tone === 'danger' ? cancelButtonRef : confirmButtonRef;
      startTarget.current?.focus();
    }

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        settle(false);
        return;
      }
      if (event.key === 'Tab') {
        const focusables = dialogRef.current?.querySelectorAll('button, input');
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = previousOverflow;
      if (previousActive instanceof HTMLElement) previousActive.focus();
    };
  }, [dialog, settle]);

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      {toasts.length > 0 && (
        <div className={styles.toastRegion} aria-live="polite" aria-atomic="false">
          {toasts.map((toastItem) => (
            <div
              key={toastItem.id}
              className={`${styles.toast} ${styles[toastItem.kind] || ''}`}
              role={toastItem.kind === 'error' ? 'alert' : 'status'}
            >
              <span className={styles.toastMessage}>{toastItem.message}</span>
              <button
                type="button"
                className={styles.toastDismiss}
                onClick={() => dismiss(toastItem.id)}
                aria-label={t('feedback.toast.dismiss', lang)}
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}

      {dialog && (
        <div
          className={styles.backdrop}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) settle(false);
          }}
        >
          <div
            ref={dialogRef}
            className={styles.dialog}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="feedback-dialog-title"
            aria-describedby="feedback-dialog-message"
          >
            <h2 id="feedback-dialog-title" className={styles.dialogTitle}>
              {dialog.title}
            </h2>
            <p id="feedback-dialog-message" className={styles.dialogMessage}>
              {dialog.message}
            </p>
            {dialog.requireTypedConfirmation && (
              <div className={styles.dialogTypedConfirm}>
                <label htmlFor="feedback-dialog-typed-input" className={styles.dialogTypedLabel}>
                  {dialog.requireTypedConfirmation.label}
                </label>
                <input
                  id="feedback-dialog-typed-input"
                  ref={typedInputRef}
                  type="text"
                  autoComplete="off"
                  className={styles.dialogTypedInput}
                  value={typedValue}
                  onChange={(event) => setTypedValue(event.target.value)}
                  onKeyDown={(event) => {
                    if (
                      event.key === 'Enter' &&
                      typedValue.trim() === String(dialog.requireTypedConfirmation.expected)
                    ) {
                      settle(true);
                    }
                  }}
                />
              </div>
            )}
            <div className={styles.dialogActions}>
              <button
                ref={cancelButtonRef}
                type="button"
                className={styles.dialogCancel}
                onClick={() => settle(false)}
              >
                {dialog.cancelLabel || t('feedback.confirm.cancel', lang)}
              </button>
              <button
                ref={confirmButtonRef}
                type="button"
                className={`${styles.dialogConfirm} ${
                  dialog.tone === 'danger' ? styles.dialogConfirmDanger : ''
                }`}
                disabled={
                  dialog.requireTypedConfirmation &&
                  typedValue.trim() !== String(dialog.requireTypedConfirmation.expected)
                }
                onClick={() => settle(true)}
              >
                {dialog.confirmLabel || t('feedback.confirm.ok', lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </FeedbackContext.Provider>
  );
}

export { Spinner };
export default FeedbackProvider;
